/* eslint-disable check-preloaders/no-promise-all */
import { getLogger } from '@confluence/logger';
import { setLoadableErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { getClientStream } from '@confluence/ssr-data-streaming/entry-points/client.ts';
import { getServerStream } from '@confluence/ssr-data-streaming/entry-points/server.ts';

import { defer } from './defer.ts';
import type { MustPreloadLoadableName } from './MustPreloadLoadableNamesNew.ts';

const logger = getLogger('Loadable');

const LOADING_TIMEOUT = process.env.NODE_ENV === 'production' ? 8500 : 30000;

let pendingPreloadInvokes: { [nameOrId: string]: () => void } = {};
let resolveMap: {
	[nameOrId: string]: {
		resolve: Function;
		promise: Promise<any>;
		cancel: Function;
	};
} = {};
const pendingImmediatePreloadInvokes = new Set<string>();
const loadableSet = new Set<string>();
const emptyLoadableSet = new Set<string>();

const EVENT_STREAM_NAME = 'loadables';
const ENQUEUE_EVENT_KEY = 'e'; // Signal to add a loadable to the preload queue
const DEQUEUE_EVENT_KEY = 'd'; // Signal to remove a loadable from the preload queue (e.g., empty loadables)
let clientInitialized = false;

function enqueueLoadable(id: string) {
	if (resolveMap[id]) return;
	const { resolve, promise, cancel } = defer(LOADING_TIMEOUT, () => {
		logger.error`Loadable initialization timed out: ${id}`;
		(window.__LOADABLE_TIMEOUTS__ ||= []).push(id);
	});
	resolveMap[id] = { resolve, promise, cancel };

	// Enqueue can happen before or after a loadable is discovered.
	// If enqueue happens before, we need to invoke the preload immediately when we discover the loadable.
	pendingImmediatePreloadInvokes.add(id);
	// If enqueue happens after, we need to call preload() on previously discovered loadable.
	pendingPreloadInvokes[id]?.();
}

function dequeueLoadable(id: string) {
	if (!resolveMap[id]) {
		return;
	}
	// At this point we have already scheduled the loadable to be loaded.
	// Whether it is actually loaded depends on if we have discovered it yet.
	// So all we can do is remove it from the resolveMap so SPA won't wait for it.
	// And if we have not discovered it yet we will skip loading it.
	resolveMap[id].cancel();
	delete resolveMap[id];
	pendingImmediatePreloadInvokes.delete(id);
	delete pendingPreloadInvokes[id];
}

function initializeLoadable() {
	// We might not know if streaming is enabled at this point.
	// Listen to the events anyway.
	if (!clientInitialized) {
		clientInitialized = true;
		getClientStream(EVENT_STREAM_NAME).addListener((event) => {
			const enqueueId = event[ENQUEUE_EVENT_KEY];
			const dequeueId = event[DEQUEUE_EVENT_KEY];

			if (enqueueId) {
				enqueueLoadable(enqueueId);
			} else if (dequeueId) {
				dequeueLoadable(dequeueId);
			}
		});
	}

	// enqueueLoadable is safe to call multiple times.
	// Skip empty loadables - they have no content and should not block preloading
	const emptyLoadables = window.__EMPTY_LOADABLE__ || [];
	for (const nameOrId of window.__LOADABLE__ || []) {
		// Check if this is an empty loadable by extracting component name
		const componentName = nameOrId.split(':')[0];
		if (!emptyLoadables.includes(componentName)) {
			enqueueLoadable(nameOrId);
		}
	}
}

export async function preloadLoadable(): Promise<any> {
	initializeLoadable();

	// Re-check for empty loadables that may have been detected after initializeLoadable() ran
	// This catches empty loadables whose dequeue events arrived late
	const emptyLoadables = window.__EMPTY_LOADABLE__ || [];
	const emptyComponentNames = new Set(emptyLoadables);

	for (const id in resolveMap) {
		if (!resolveMap[id]) {
			continue;
		}
		const componentName = id.split(':')[0];
		if (emptyComponentNames.has(componentName)) {
			resolveMap[id].cancel();
			delete resolveMap[id];
		}
	}

	try {
		await Promise.all(Object.values(resolveMap).map(({ promise }) => promise));
		if (window.__LOADABLE_TIMEOUTS__?.length) {
			const error = new Error(`Loadable initialization timeout`);
			const loadableIds = window.__LOADABLE_TIMEOUTS__;
			setLoadableErrorAttributes(error, { loadableIds });
			// NOTE: apparently there's plenty of timeout errors happen during the normal flow of operation.
			// While we need to troubleshoot and fix them eventually, we don't want to flood the monitoring,
			// but we also want to track and alert upon them. Hence using a separate attribution.
			if (navigator.onLine) {
				getMonitoringClient().submitError(error, { attribution: 'backbone-loadable' });
			}
		}
	} finally {
		// Note here we don't clear resolveMap logic is using this to avoid load loadable twice.
		pendingPreloadInvokes = {};
		pendingImmediatePreloadInvokes.clear();
	}
}

export function preloadIfNeeded(
	id: string | undefined,
	name: string | undefined,
	preload: () => Promise<any>,
) {
	initializeLoadable();
	const invokePreload = () => {
		if (id) delete pendingPreloadInvokes[id];
		if (name) delete pendingPreloadInvokes[name];

		if ((id && id in resolveMap) || (name && name in resolveMap)) {
			const resolve = () => {
				// Note that due to the _mustPreloadWhenRenderingSPA logic the loadable may appear
				// in the list of loadables both under id and under name. If we have 2 entries for
				// the loadable, we should resolve both.
				if (id && resolveMap[id]) resolveMap[id].resolve();
				if (name && resolveMap[name]) resolveMap[name].resolve();
			};
			const startTime = performance.now();
			void preload().then(
				() => {
					logger.debug`SSR Preload: ${name || id} in ${Math.floor(performance.now() - startTime)}ms`;
					resolve();
				},
				(e) => {
					logger.error(e);
					resolve();
				},
			);
		}
	};

	if (
		(id && pendingImmediatePreloadInvokes.has(id)) ||
		(name && pendingImmediatePreloadInvokes.has(name))
	) {
		invokePreload();
	} else {
		// __LOADABLE__ is not available when we execute code splits early than DOMContentLoaded
		// As soon as loadable are imported preloadIfNeeded gonna run because they are defined at module level.
		// In these cases we don't have a resolveMap to work with.
		// So we are saving the ids and invoke later when we get signal from SSR either stream or final __LOADABLE__
		if (id) pendingPreloadInvokes[id] = invokePreload;
		if (name) pendingPreloadInvokes[name] = invokePreload;
	}
}

export function addLoadableId(id?: string) {
	if (!id) return;

	if (window?.__SSR_REACT_STREAMING__) {
		if (!loadableSet.has(id)) {
			getServerStream(EVENT_STREAM_NAME).emit({ [ENQUEUE_EVENT_KEY]: id });
		}
	}
	loadableSet.add(id);
}

export function addEmptyLoadableId(id?: string) {
	if (!id) {
		return;
	}
	if (window?.__SSR_REACT_STREAMING__) {
		if (!emptyLoadableSet.has(id)) {
			getServerStream(EVENT_STREAM_NAME).emit({ [DEQUEUE_EVENT_KEY]: id });
		}
	}
	emptyLoadableSet.add(id);
}

export function getLoadableIds(): string[] {
	return Array.from(loadableSet);
}

export function getEmptyLoadableIds(): string[] {
	return Array.from(emptyLoadableSet);
}

/**
 * Allow components declare loose dependencies on other loadable components that
 * would tell Loadable infrastructure to preload these components. It means that
 * component A can declare that it needs component B preloaded, when component A
 * is rendered on the server.
 *
 * This is functional way to declare dependency between code that runs on SSR and
 * code that needs to be preloaded in the browser, similar to Loadable option
 * `_mustPreloadWhenRenderingSPA`.
 *
 * NOTE: this approach is needed, because we cannot have direct dependencies from
 * next to classic code so it serves as a "clean" hack allowing gradual migration
 * to next.
 *
 * @param id Loadable id / name
 */
export function mustPreloadWhenRenderingSPA(loadableIds: Array<string | MustPreloadLoadableName>) {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		loadableIds.forEach((id) => addLoadableId(id));
	}
}

export function _resetOnlyForTest() {
	resolveMap = {};
	pendingPreloadInvokes = {};
	loadableSet.clear();
	emptyLoadableSet.clear();
	pendingImmediatePreloadInvokes.clear();
	clientInitialized = false;
}
